import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, CardView, Spacer, Switch } from 'design-system';
import Amount from 'design-system/Amount';
import AmountPill from 'design-system/AmountPill';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { AUTOSAVE_DEFAULT_MONTHLY_AMOUNT, AUTOSAVE_DEFAULT_WEEKLY_AMOUNT, AUTOSAVE_MONTHLY_INVESTMENT_AMOUNTS, AUTOSAVE_WEEKLY_INVESTMENT_AMOUNTS, } from 'features/pots/constants';
import { useUpdateAutosaveCollectionFrequency } from 'features/pots/hooks/useUpdateAutosaveCollectionFrequency';
import { roundUpOptions, SavingsPlanRuleType } from 'features/pots/types';
import { getPeriodFromRRule } from 'features/pots/utils';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
const AutosaveChooseSavingRules = withAnimatedNavigationHeader(({ route, scrollHandler, navigation }) => {
    const styles = useStyles(styleSet);
    const autosaveId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const automated = useAppSelector((state) => state.pots.savingsPlan?.automated);
    const roundUpMultiplier = useAppSelector((state) => state.pots.savingsPlan?.roundUpMultiplier);
    const { updateCollectionFrequency, fetching } = useUpdateAutosaveCollectionFrequency();
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const isMonthly = getPeriodFromRRule(route.params.collectionFrequency.rrule) === 'Monthly';
    const amountOptions = isMonthly ? AUTOSAVE_MONTHLY_INVESTMENT_AMOUNTS : AUTOSAVE_WEEKLY_INVESTMENT_AMOUNTS;
    const defaultAmount = isMonthly ? AUTOSAVE_DEFAULT_MONTHLY_AMOUNT : AUTOSAVE_DEFAULT_WEEKLY_AMOUNT;
    const [state, setState] = useState({
        automated: {
            show: !!automated,
        },
        roundUps: roundUpMultiplier
            ? {
                show: true,
                multiplier: roundUpMultiplier,
            }
            : {
                show: false,
                multiplier: 1,
            },
        custom: {
            show: false,
            amount: defaultAmount,
        },
    });
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    }, []);
    useEffect(() => {
        Amplitude.logEvent('Choose Autosave Rules');
    }, []);
    const handleSelection = useCallback((key) => () => {
        setState((oldState) => ({
            ...oldState,
            [key]: {
                ...oldState[key],
                show: !oldState[key].show,
            },
        }));
    }, []);
    const onChangeRound = useCallback((round) => async () => {
        setState((oldState) => ({
            ...oldState,
            roundUps: {
                ...oldState.roundUps,
                multiplier: round,
            },
        }));
    }, []);
    const onChangeAmount = useCallback((amount) => async () => {
        setState((oldState) => ({
            ...oldState,
            custom: {
                ...oldState.custom,
                amount,
            },
        }));
    }, []);
    const handleCustomDeposit = useCallback(() => {
        navigation.navigate('AutosaveChooseCustomAmount', {
            onContinue: (amount) => {
                onChangeAmount(amount)();
            },
            isMonthly,
            initialAmount: state.custom.amount,
        });
    }, [isMonthly, onChangeAmount, state.custom.amount]);
    const handleContinue = useCallback(() => {
        if (!state.automated.show && !state.custom.show && !state.roundUps.show) {
            return;
        }
        const rules = [];
        if (state.automated.show) {
            rules.push({
                type: SavingsPlanRuleType.AUTOMATED,
            });
        }
        if (state.roundUps.show && state.roundUps.multiplier) {
            rules.push({
                type: SavingsPlanRuleType.ROUND_UP,
                roundUpMultiplier: state.roundUps.multiplier,
            });
        }
        if (state.custom.show && state.custom.amount) {
            rules.push({
                type: SavingsPlanRuleType.CUSTOM,
                amount: {
                    value: state.custom.amount,
                    currency: 'GBP',
                },
            });
        }
        if (autosaveId) {
            updateCollectionFrequency(autosaveId, route.params.collectionFrequency.rrule, rules);
            return;
        }
        navigation.navigateDeprecated('AutosaveDistribution', {
            rules,
            collectionFrequency: route.params.collectionFrequency,
        });
    }, [
        autosaveId,
        route.params.collectionFrequency,
        state.automated.show,
        state.custom.amount,
        state.custom.show,
        state.roundUps.multiplier,
        state.roundUps.show,
        updateCollectionFrequency,
    ]);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <ScreenInfoHeader title="Choose your saving rules" subTitle="You can enable as many as you want or just select one"/>
          <CardView noMarginTop noMarginBottom>
            <View style={styles.titleContainer}>
              <View>
                <Text Text-12 Primary>
                  RECOMMENDED
                </Text>
                <Text Text-16>AI savings</Text>
              </View>
              <Switch testID="aiSavingsSwitch" accessibilityRole="switch" value={state.automated.show} onValueChange={handleSelection('automated')}/>
            </View>
            <View style={styles.descView}>
              <Text TextThin-14 Secondary>
                Emma will calculate the perfect amount to set aside based on your spending and current account balance.
                This is great if you don’t know how to get started saving!
              </Text>
            </View>
          </CardView>

          <Spacer.H16 />

          <CardView noMarginTop noMarginBottom>
            <View style={styles.titleContainer}>
              <Text Text-16>Round Ups</Text>
              <Switch testID="roundUpsSwitch" accessibilityRole="switch" value={state.roundUps.show} onValueChange={handleSelection('roundUps')}/>
            </View>
            <View style={styles.descView}>
              <Text TextThin-14 Secondary>
                Emma will round up your transactions from all active linked accounts to the nearest £1 and transfer the
                spare change to your savings pots
              </Text>
            </View>
            {state.roundUps.show ? (<View testID="roundUpMain">
                <View style={styles.main}>
                  <Text Text-14>Use the multiplier to save faster:</Text>
                  <Spacer.H16 />
                </View>
                <View style={styles.buttonContainer} pointerEvents="box-none">
                  {roundUpOptions.map((option) => (<Button flex square key={String(option)} title={`${option}x`} style={styles.roundBtn} onPress={onChangeRound(option)} {...(state.roundUps.multiplier === option
                ? { brandLight: true }
                : { secondary: true, gray: true })}/>))}
                </View>
              </View>) : null}
          </CardView>

          <Spacer.H16 />

          <CardView noMarginTop noMarginBottom>
            <View style={styles.titleContainer}>
              <Text Text-16>Custom deposit</Text>
              <Switch testID="customSwitch" accessibilityRole="switch" value={state.custom.show} onValueChange={handleSelection('custom')}/>
            </View>
            <View style={styles.descView}>
              <Text TextThin-14 Secondary>
                Set up a custom amount to save every{' '}
                {getPeriodFromRRule(route.params.collectionFrequency.rrule) === 'Monthly' ? 'month' : 'week'}
              </Text>
            </View>
            {state.custom.show ? (<View testID="customMain">
                <View style={styles.main}>
                  <View style={styles.amountContainer}>
                    <Text Text-14>Amount:</Text>
                    <Amount value={{ value: state.custom.amount, currency: 'GBP' }} Numbers-16 showFraction={false}/>
                  </View>
                  <Spacer.H16 />
                </View>
                <View style={styles.amountBtnContainer} pointerEvents="box-none">
                  {amountOptions.map((option) => (<AmountPill value={option} currency="GBP" key={String(option)} style={styles.roundBtn} onPress={onChangeAmount(option)} selected={state.custom.amount === option}/>))}
                </View>
                <View style={styles.customAmountBtnWrapper} pointerEvents="box-none">
                  <Button flex square title="Enter custom amount" {...(amountOptions.includes(state.custom.amount)
            ? { secondary: true, gray: true }
            : { brandLight: true })} onPress={handleCustomDeposit}/>
                </View>
              </View>) : null}
          </CardView>
        </Animated.ScrollView>
        <Button brand floating title="Continue" isFetching={fetching} onPress={handleContinue} disabled={!state.automated.show && !state.custom.show && !state.roundUps.show}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    titleContainer: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
        paddingBottom: rem(12),
        justifyContent: 'space-between',
    },
    descView: {
        padding: rem(16),
        paddingTop: 0,
    },
    main: {
        paddingTop: rem(16),
        borderTopWidth: rem(1),
        paddingHorizontal: rem(16),
        borderTopColor: colors.background.dark,
    },
    amountBtnContainer: {
        flexDirection: 'row',
        paddingBottom: rem(8),
        paddingHorizontal: rem(12),
    },
    buttonContainer: {
        flexDirection: 'row',
        paddingBottom: rem(16),
        paddingHorizontal: rem(12),
    },
    roundBtn: {
        flex: 1,
        marginHorizontal: rem(4),
    },
    amountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: rem(8),
        justifyContent: 'space-between',
    },
    customAmountBtnWrapper: {
        paddingBottom: rem(16),
        paddingHorizontal: rem(16),
    },
}));
export default AutosaveChooseSavingRules;
